import { type ApiErrorBody, type PublicUser, type SessionResponse } from '@veriverse/shared';
import { clientConfig } from '../config.js';

/**
 * REST client. The access token lives in memory only (never localStorage): a page reload gets a
 * fresh one from the httpOnly refresh cookie via `POST /auth/refresh` (ADR-0004).
 */

const REFRESH_MARGIN_MS = 30_000;

let accessToken: string | undefined;
let expiresAt = 0;
let inflight: Promise<SessionResponse | undefined> | undefined;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const url = (path: string) => `${clientConfig.serverUrl}${path}`;

/** Browser navigation to GitHub's sign-in; the server redirects back to this app afterwards. */
export function startGitHubLogin(): void {
  window.location.assign(url('/auth/github/login'));
}

/**
 * Exchanges the refresh cookie for a new access token. Concurrent callers share one request,
 * which matters because refresh tokens rotate and a second parallel call would count as reuse.
 */
export function refreshSession(): Promise<SessionResponse | undefined> {
  inflight ??= (async () => {
    try {
      const res = await fetch(url('/auth/refresh'), { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        accessToken = undefined;
        return undefined;
      }
      const session = (await res.json()) as SessionResponse;
      accessToken = session.accessToken;
      expiresAt = Date.now() + session.expiresInSeconds * 1000;
      return session;
    } catch {
      return undefined;
    } finally {
      inflight = undefined;
    }
  })();
  return inflight;
}

/** A valid access token, refreshing first when it is missing or about to expire. */
export async function getAccessToken(): Promise<string | undefined> {
  if (!accessToken || Date.now() > expiresAt - REFRESH_MARGIN_MS) await refreshSession();
  return accessToken;
}

export async function logout(): Promise<void> {
  accessToken = undefined;
  try {
    await fetch(url('/auth/logout'), { method: 'POST', credentials: 'include' });
  } catch {
    /* offline: the cookie will expire on its own */
  }
}

interface RequestInit2 {
  method?: string;
  /** JSON-encoded when it is a plain object. Use `raw` for files. */
  body?: unknown;
  raw?: { data: Blob; contentType: string };
}

/**
 * Authenticated request that returns the raw `Response` without interpreting the status. Used
 * where a non-2xx body carries data (map validation issues) or the answer is a file.
 */
export async function apiResponse(path: string, init: RequestInit2 = {}): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new ApiError(401, 'unauthorized', 'Not signed in');
  const json = init.body !== undefined;
  return fetch(url(path), {
    method: init.method ?? 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(json && { 'Content-Type': 'application/json' }),
      ...(init.raw && { 'Content-Type': init.raw.contentType }),
    },
    ...(json && { body: JSON.stringify(init.body) }),
    ...(init.raw && { body: init.raw.data }),
  });
}

/** Authenticated JSON request. Throws `ApiError` for non-2xx responses. */
export async function api<T = void>(path: string, init: RequestInit2 = {}): Promise<T> {
  const res = await apiResponse(path, init);
  if (!res.ok) {
    let body: Partial<ApiErrorBody> | undefined;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      /* non-JSON error */
    }
    throw new ApiError(
      res.status,
      body?.error?.code ?? 'error',
      body?.error?.message ?? res.statusText,
    );
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/** Absolute URL of an uploaded image (`/assets/{hash}.png`). */
export const assetSrc = (path: string): string => url(path);

export type { PublicUser };
